import { type ReactNode, useMemo } from "react";
import type { PersonalizedFertilizerPdpResponse } from "@halm-club/halm-shared";
import { ProductImageCarousel } from "../../../../shared/pdp/components/ProductImageCarousel";
import { ProductPrice } from "../../../../shared/pdp/components/ProductPrice";
import {
  readBooleanHalmParameter,
  readHalmParametersQueryString,
} from "../../../../shared/pdp/utils/halmParameters";
import { formatMoney } from "../../../../shared/pdp/utils/pricing";
import "./FertilizerBuyBox.css";
import { QuizSummary } from "../../../../shared/pdp/components/QuizSummary";
import {
  getFertilizerPurchaseOption,
  getSelectedFertilizerVariant,
} from "../../purchaseOptions";

type FertilizerVariant = PersonalizedFertilizerPdpResponse["variants"][number];
type BuyboxFeature =
  PersonalizedFertilizerPdpResponse["buyboxFeatures"][number];

type FertilizerBuyBoxProps = {
  buyboxFeatures: BuyboxFeature[];
  children?: ReactNode;
  compareAtPrice: number;
  description: string;
  imageSources: string[];
  onLawnSizeChange: (lawnSize: number) => void;
  price: number;
  selectedLawnSize: number;
  title: string;
  variants: FertilizerVariant[];
};

const getAnalyzedLawnSize = () => {
  const params = new URLSearchParams(readHalmParametersQueryString());
  const lawnSizeParam = params.get("lawn_size");
  const adjustedLawnSizeParam = params.get("adjusted_lawn_size");

  if (!lawnSizeParam || !adjustedLawnSizeParam) {
    return null;
  }

  const lawnSize = Number(lawnSizeParam);
  const adjustedLawnSize = Number(adjustedLawnSizeParam);

  if (
    !Number.isFinite(lawnSize) ||
    !Number.isFinite(adjustedLawnSize) ||
    lawnSize <= 0 ||
    adjustedLawnSize <= 0 ||
    lawnSize === adjustedLawnSize
  ) {
    return null;
  }

  return new Intl.NumberFormat("de-DE").format(lawnSize);
};

export function FertilizerBuyBox({
  buyboxFeatures,
  children,
  compareAtPrice,
  description,
  imageSources,
  onLawnSizeChange,
  price,
  selectedLawnSize,
  title,
  variants,
}: FertilizerBuyBoxProps) {
  const selectedVariant = getSelectedFertilizerVariant(
    variants,
    selectedLawnSize,
  );
  const isOrganic = useMemo(() => readBooleanHalmParameter("organic"), []);
  const analyzedLawnSize = useMemo(getAnalyzedLawnSize, []);

  return (
    <div className="personalized-fertilizer-pdp__buybox">
      <div className="personalized-fertilizer-pdp__product-card">
        <div className="personalized-fertilizer-pdp__product-summary">
          <ProductImageCarousel imageAlt={title} imageSources={imageSources} />

          <div className="personalized-fertilizer-pdp__product-main">
            <div className="personalized-fertilizer-pdp__title-row">
              <div className="personalized-fertilizer-pdp__title-copy">
                <h1
                  className="personalized-fertilizer-pdp__title"
                  data-test="fertilizer-title"
                >
                  {title}
                </h1>
                <p className="personalized-fertilizer-pdp__description">
                  {description}
                </p>
              </div>
              <ProductPrice
                compareAtPrice={compareAtPrice}
                price={price}
                className="desktop"
              />
            </div>

            <QuizSummary pageType="fertilizer" />

            {buyboxFeatures.length || isOrganic ? (
              <ul className="personalized-fertilizer-pdp__features">
                {buyboxFeatures.map((feature, index) => (
                  <li
                    className="personalized-fertilizer-pdp__feature"
                    key={`${feature.text}-${index}`}
                  >
                    <span
                      aria-hidden="true"
                      className="material-symbols-rounded personalized-fertilizer-pdp__feature-icon"
                    >
                      {feature.materialSymbolName}
                    </span>
                    <span>{feature.text}</span>
                  </li>
                ))}
                {isOrganic && (
                  <li className="personalized-fertilizer-pdp__feature">
                    <span className="personalized-fertilizer-pdp__feature-bio-icon-wrapper">
                      <img
                        alt=""
                        className="personalized-fertilizer-pdp__feature-bio-icon"
                        src="https://cdn.shopify.com/s/files/1/0567/4365/7562/files/bio-logo.png?v=1692737264"
                      />
                    </span>
                    <span>100% natürliche Inhaltsstoffe</span>
                  </li>
                )}
              </ul>
            ) : null}

            <div className="personalized-fertilizer-divider">
              <div></div>
            </div>
            <div className="personalized-fertilizer-pdp__variant-footer">
              <div className="personalized-fertilizer-pdp__variant-row">
                <div className="personalized-fertilizer-pdp__variant-copy">
                  <span className="personalized-fertilizer-pdp__variant-size">
                    <span>Für bis zu </span>
                    <strong>
                      {selectedVariant ? selectedVariant.lawnSize : "-"}m²
                    </strong>
                  </span>
                  <label className="personalized-fertilizer-pdp__variant-label">
                    <span>Ändern</span>
                    <select
                      aria-label="Rasengröße ändern"
                      className="personalized-fertilizer-pdp__variant-select"
                      data-test="fertilizer-size-select"
                      onChange={(event) =>
                        onLawnSizeChange(Number(event.target.value))
                      }
                      value={selectedLawnSize}
                    >
                      {variants.map((variant) => {
                        const onetimePurchaseOption =
                          getFertilizerPurchaseOption(variant, "ONETIME");

                        return (
                          <option
                            key={variant.lawnSize}
                            value={variant.lawnSize}
                          >
                            Für bis zu {variant.lawnSize} m² -{" "}
                            {onetimePurchaseOption
                              ? formatMoney(onetimePurchaseOption.priceCents)
                              : "–"}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                </div>
                <span className="personalized-fertilizer-pdp__variant-price">
                  {formatMoney(price)}
                </span>
              </div>
              {analyzedLawnSize ? (
                <span className="personalized-fertilizer-pdp__analyzed-lawn-size">
                  Aus deiner Rasenanalyse: {analyzedLawnSize} m²
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="personalized-fertilizer-pdp__soil-slot">{children}</div>
      </div>
    </div>
  );
}
